import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import * as actionCreators from '../../actions/actionCreators';

import {
    trackMapMarkerClick,
    trackLocationListMarkerClick,
    trackMapFilterSelect,
    trackMapFindClosest
} from '../../utils/analytics';

import Map from '../Map/Map';
import MapSearch from '../MapSearch/MapSearch';
import MapList from '../MapList/MapList';
import MapFilter from '../MapFilter/MapFilter';
import MapClosestButton from '../MapClosestButton/MapClosestButton';
import MobilePopup from '../MobilePopup/MobilePopup';
import InfoWindow from '../InfoWindow/InfoWindow';
import { updateFilter } from '../../utils/updateFilter';

import { DEFAULT_CENTER, INITIAL_ZOOM } from '../../constants/map';

class Layout extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isFilterOpen: true,
            mobileIsFilterOpen: true,
        };
    }

    componentDidMount() {
        this.props.setExternalGeolocation(this.props.externalGeolocation);
        this.props.setFilter(this.getFormattedServices(this.props.language));

        window.addEventListener('popstate', this.handlePopState);
        // listen for filter changes triggered from outside the react app (i.e. currency exchange block)
        window.addEventListener(
            'bankomatFilterChange',
            this.handleExternalFilterChange
        );

        if (this.props.presetFilter) {
            this.updateFilterFromPreset(this.props.presetFilter);
        }
    }

    updateFilterFromPreset(id) {
        const filter = this.getFormattedServices(this.props.language);
        const updatedFilter = updateFilter(filter, id);

        this.props.setFilter(updatedFilter);
    }

    getFormattedServices(language) {
        const updatedServices = [...this.props.services];
        return [
            ...updatedServices.map((item) => {
                return {
                    active: false,
                    id: `${item.value}`,
                    label: item[`name_${language}`],
                    value: item.value,
                    options: item.denominations.split(',').map((x) => {
                        return {
                            active: false,
                            id: `${item.value}_${x}`,
                            label: `${x} ${item.value} sedlar`,
                            value: x
                        };
                    })
                };
            })
        ];
    }

    handleFilterToggle = () => {
        this.setState((prevState) => ({
            isFilterOpen: !prevState.isFilterOpen
        }));
    };

    handleMobileFilterToggle = () => {
        this.setState((prevState) => ({
            mobileIsFilterOpen: !prevState.mobileIsFilterOpen
        }));
    };

    handlePopState = (e) => {
        if (e.state && e.state.id && e.state.zoom && e.state.center) {
            const marker = this.props.items.find(
                (item) => item.identifier === e.state.id
            );

            this.props.setMarker(marker);
            this.props.setZoom(parseInt(e.state.zoom));
            this.props.setCenter(e.state.center);
        }
    };

    showList = () => {
        const { setListActive } = this.props;

        setListActive(true);
    };

    hideList = () => {
        const { setListActive } = this.props;

        setListActive(false);
    };

    toggleList = () => {
        const { setListActive, listActive } = this.props;

        setListActive(!listActive);
    };

    handleCloseInfoWindow = () => {
        this.props.setMarker({});
        document.body.classList.remove('feedback-active');
    };

    returnFilter = () => {
        return this.props.filter;
    };

    createQueryString = (filters) => {
        const filterObject = {};
        filters.forEach((filter) => {
            const currency = filter.value;
            const activeDenominations = filter.options
                .filter((option) => option.active)
                .map((option) => parseInt(option.value));

            if (activeDenominations.length > 0) {
                filterObject[currency] = activeDenominations;
            }
        });

        const filterJson = JSON.stringify(filterObject);
        const params = new URLSearchParams();
        params.append('filter', filterJson);

        return params.toString();
    };

    updateUrlWithFilters = (filters) => {
        const queryString = this.createQueryString(filters);
        const newUrl = `${window.location.pathname}?${queryString}`;
        history.pushState({ path: newUrl }, '', newUrl);
    };

    handleFilterChange = (id, denomination) => {
        const { filter, setFilter } = this.props;
        const updatedFilter = updateFilter(filter, id, denomination);

        this.updateUrlWithFilters(updatedFilter);
        setFilter(updatedFilter);
    };

    handleExternalFilterChange = (e) => {
        this.props.infowindow.close();
        this.props.setMarker({});

        this.props.setCenter(DEFAULT_CENTER);
        this.props.setZoom(INITIAL_ZOOM);

        const filter = this.getFormattedServices(this.props.language);
        const updatedFilter = updateFilter(
            filter,
            e.detail.filter.toLowerCase()
        );

        this.props.setFilter(updatedFilter);
    };

    handleLocationListMarkerClick = (marker) => {
        trackLocationListMarkerClick(marker);
        this.handleMarkerClick(marker);
    };

    handleMapMarkerClick = (marker) => {
        trackMapMarkerClick(marker);
        this.handleMarkerClick(marker);
    };

    handleMarkerClick = (marker) => {
        const { zoom, center } = this.props;

        this.props.setMarker(marker);

        const id = marker.identifier;
        const newUrl = `?marker=${id}&zoom=${zoom}&center=${center.lat},${center.lng}`;
        history.pushState(
            {
                id: id,
                zoom: zoom,
                center: center
            },
            null,
            newUrl
        );
    };

    componentWillMount() {
        document.addEventListener('mousedown', this.handleClick, false);
    }

    componentWillUnmount() {
        document.addEventListener('mousedown', this.handleClick, false);
    }

    handleClick = (e) => {
        if (!this.node) {
            return;
        }

        if (this.node.contains(e.target)) {
            return;
        }

        this.hideList();
    };

    reposition = () => {
        const newUrl = `${window.location.origin}${window.location.pathname}`;
        history.pushState({}, null, newUrl);
        this.props.setCenterToCurrentCoordinates();

        trackMapFindClosest();
    };

    render() {
        const {
            items,
            services,
            activeMarker,
            labels,
            googleMapKey,
            language,
            coinMachineUrl,
            listActive,
            showFilters,
            dontShowAllFilters,
            showClosestButton,
            presetFilter
        } = this.props;

        const { isFilterOpen, mobileIsFilterOpen } = this.state;

        const mapProps = {
            googleMapKey,
            labels,
            language,
            coinMachineUrl,
            services,
            showFilters,
            presetFilter
        };

        const options = this.getFormattedServices(language);

        const mapClasses = classNames('Layout__Map', {
            'Layout__Map--InfoWindowOpen':
                Object.keys(activeMarker).length !== 0,
            'Layout__Map--ListActive': listActive
        });
        const controlClasses = classNames('Layout__Controls', {
            'Layout__Controls--InfoWindowOpen':
                Object.keys(activeMarker).length !== 0,
            'Layout__Controls--Open': isFilterOpen
        });
        const largeFilterToggleClasses = classNames('Layout__ToggleContainer', {
            'Layout__ToggleContainer--Open': isFilterOpen
        });
        const mobileFilterToggleClasses = classNames(
            'Layout__FilterContainer',
            {
                'Layout__FilterContainer--Open': !mobileIsFilterOpen
            }
        );

        return (
            <div className="Layout">
                <div className={largeFilterToggleClasses}>
                    <button
                        className="Layout__LargeFilterToggle"
                        onClick={this.handleFilterToggle}
                    >
                        Visa sök och filtrering
                    </button>
                </div>
                {showFilters && (
                    <div id="filters" className={controlClasses}>
                        <span
                            className="Layout__FilterToggle"
                            onClick={this.handleFilterToggle}
                        />
                        <h3 className="Layout__FilterHeader">Hitta automat</h3>
                        {items.length > 0 && !dontShowAllFilters && (
                            <div
                                className="Layout__ListContent"
                                key="listContent"
                                ref={(node) => (this.node = node)}
                            >
                                <MapSearch
                                    googleMapKey={googleMapKey}
                                    labels={this.props.labels}
                                    showList={this.showList}
                                    hideList={this.hideList}
                                />
                                <MapList
                                    labels={this.props.labels}
                                    services={this.props.services}
                                    language={language}
                                    center={this.props.center}
                                    listActive={this.props.listActive}
                                    onMarkerClick={
                                        this.handleLocationListMarkerClick
                                    }
                                />
                            </div>
                        )}
                        <div className={mobileFilterToggleClasses}>
                            {(!dontShowAllFilters || showClosestButton) && (
                                <MapClosestButton
                                    labels={labels}
                                    openMarker={this.handleMarkerClick}
                                    getFilter={this.returnFilter}
                                />
                            )}
                            {!showClosestButton &&
                                this.props.filter.length > 0 && (
                                    <MapFilter
                                        labels={labels}
                                        options={options}
                                        filter={this.props.filter}
                                        onFilterChange={this.handleFilterChange}
                                        hideList={this.hideList}
                                    />
                                )}
                            {/* {mobileIsFilterOpen ? (
                                // Keeping this commented out for now, as it's not used
                                <span
                                    className='Layout__MobileFilterToggleSpan'
                                    onClick={this.handleMobileFilterToggle}
                                    onTouchStart={this.handleTouchStart}
                                />
                            ) : (
                                <button
                                    className='Layout__MobileFilterToggleButton'
                                    onClick={this.handleMobileFilterToggle}
                                >
                                    Visa filtrering
                                </button>
                            )} */}
                            <button
                                className="Layout__MobileFilterToggleButton"
                                onClick={this.handleMobileFilterToggle}
                            >
                                {mobileIsFilterOpen
                                    ? 'Dölj filtrering'
                                    : 'Visa filtrering'}
                            </button>
                        </div>
                    </div>
                )}

                {((!dontShowAllFilters || showClosestButton) && !showFilters) && (
                    <div className="Layout__ClosestButtonContainer">
                        <MapClosestButton
                            labels={labels}
                            openMarker={this.handleMarkerClick}
                            getFilter={this.returnFilter}
                        />
                    </div>
                )}

                <div className={mapClasses}>
                    <Map
                        {...mapProps}
                        onMarkerClick={this.handleMapMarkerClick}
                        onFilterChange={this.handleFilterChange}
                    />
                </div>

                {Object.keys(activeMarker).length !== 0 && (
                    <MobilePopup
                        labels={this.props.labels}
                        services={this.props.services}
                        language={language}
                        activeMarker={this.props.activeMarker}
                        userPos={this.props.userPos}
                        onClose={this.handleCloseInfoWindow}
                    />
                )}

                {this.props.map &&
                    this.props.infowindow &&
                    Object.keys(activeMarker).length > 0 && (
                        <InfoWindow
                            map={this.props.map}
                            infowindow={this.props.infowindow}
                            labels={this.props.labels}
                            services={this.props.services}
                            language={this.props.language}
                            marker={this.props.activeMarker}
                            userPos={this.props.userPos}
                            onClose={this.handleCloseInfoWindow}
                        />
                    )}
            </div>
        );
    }
}

Layout.defaultProps = {};

function mapStateToProps(state) {
    return {
        listActive: state.map.listActive,
        items: state.map.items,
        searchActive: state.map.searchActive,
        activeMarker: state.map.activeMarker || {},
        infowindow: state.map.infowindow,
        map: state.map.map,
        center: state.map.center,
        userPos: state.map.userPos,
        zoom: state.map.zoom,
        filter: state.map.filter
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(actionCreators, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
